import { useEffect, useMemo, useState } from 'react';
import { addDays, format, parseISO, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PreferredDaysGridEditor } from '@/components/custom/preferred-days-grid';
import { cn } from '@/lib/utils';
import { useOfferedSlotsRange } from '../hooks/use-offered-slots-range';
import { slotGridCell, slotsToWeekValue } from '../utils/slots-to-week-grid';

/** How long a slot picked elsewhere stays highlighted. */
export const FOCUS_HIGHLIGHT_MS = 10_000;

const ISO_DATE = 'yyyy-MM-dd';

/** Monday of the week containing `d`. */
function weekStartOf(d: Date): Date {
  return startOfWeek(d, { weekStartsOn: 1 });
}

/**
 * The slots the scheduling agent would offer for one provider + appointment
 * type + location, a week at a time: week navigation, then the appointment
 * types' `PreferredDaysGridEditor` in read-only mode, fed by the same
 * offerable-slots engine the voice agent uses. Every time in the week is
 * shown: it is fetched by days (`useOfferedSlotsRange`), not capped at a
 * number of slots.
 *
 * Weeks run Monday to Sunday. It starts on the current week, or, given a
 * `focusSlot`, on that slot's (clinic-local) week with the slot highlighted
 * for {@link FOCUS_HIGHLIGHT_MS}; the current week is also the earliest it
 * goes, since past slots are never offerable. Mount it fresh (or key it) to
 * start over: the scheduling drawer's dialog unmounts it on close, and the
 * campaign queue's slots panel keys it by provider and focused slot.
 */
export function OfferedSlotsWeek({
  providerId,
  appointmentTypeId,
  locationId,
  durationMinutes,
  isNewPatient,
  focusSlot,
  enabled = true,
  navClassName,
  bodyClassName,
}: {
  providerId: string;
  appointmentTypeId: string;
  locationId: string;
  /**
   * The appointment type's configured slot duration. Sizes the grid cells so a
   * 30-min type renders 30-min rows, even on an empty week.
   */
  durationMinutes?: number;
  /** Drives the same new-patient booking constraints the call would use. */
  isNewPatient: boolean;
  /** A slot to open on and highlight; the current week when absent. */
  focusSlot?: { startTime: string; endTime: string; locationTimezone: string } | undefined;
  /** Fetch only while shown. */
  enabled?: boolean;
  navClassName?: string;
  bodyClassName?: string;
}): React.ReactElement {
  const [currentWeekStart] = useState<Date>(() => weekStartOf(new Date()));
  const focus = useMemo(() => (focusSlot ? slotGridCell(focusSlot) : null), [focusSlot]);
  const focusWeek = focus ? weekStartOf(parseISO(focus.date)) : null;
  const [weekStart, setWeekStart] = useState<Date>(() =>
    focusWeek && focusWeek > currentWeekStart ? focusWeek : currentWeekStart
  );
  const [highlighting, setHighlighting] = useState(!!focus);
  useEffect(() => {
    if (!highlighting) return;
    const timer = setTimeout(() => setHighlighting(false), FOCUS_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlighting]);

  const weekEnd = addDays(weekStart, 6);
  const startDate = format(weekStart, ISO_DATE);
  const atCurrentWeek = startDate === format(currentWeekStart, ISO_DATE);

  // Mon→Sun "M/d" labels for the grid header.
  const columnDateLabels = useMemo(
    () => Array.from({ length: 7 }, (_, i) => format(addDays(weekStart, i), 'M/d')),
    [weekStart]
  );

  // The whole week, however many times it holds: asked by days, never cut at
  // a slot count (a busy week is more than one request's worth).
  const { data, isLoading, isError, error } = useOfferedSlotsRange(
    { providerId, appointmentTypeId, locationId, startDate, days: 7, isNewPatient },
    enabled
  );

  const slots = data?.slots ?? [];
  const weekValue = useMemo(
    () => slotsToWeekValue(slots, durationMinutes),
    [slots, durationMinutes]
  );
  const hasSlots = weekValue.days.length > 0;

  return (
    <>
      <div className={cn('flex items-center justify-between', navClassName)}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setWeekStart((w) => addDays(w, -7))}
          disabled={atCurrentWeek}
          title={atCurrentWeek ? 'Already showing the current week' : 'Previous week'}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
          </span>
          {!atCurrentWeek && (
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(currentWeekStart)}>
              This week
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setWeekStart((w) => addDays(w, 7))}
          title="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className={bodyClassName}>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Computing offered times…</span>
          </div>
        ) : isError ? (
          <div className="py-16 text-center text-sm text-destructive">
            Couldn’t load offered slots
            {error instanceof Error ? `: ${error.message}` : ''}.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {!hasSlots && (
              <div className="rounded-md border border-dashed py-3 text-center text-sm text-muted-foreground">
                No open times this week — try the next week.
              </div>
            )}
            <PreferredDaysGridEditor
              readOnly
              showViewControls={false}
              value={weekValue}
              onChange={() => {}}
              columnDateLabels={columnDateLabels}
              // Only while the slot's own week is shown.
              highlight={
                highlighting && focus && focusWeek && format(focusWeek, ISO_DATE) === startDate
                  ? focus
                  : null
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
